// Custom Field Types

export type FieldType = 'text' | 'number' | 'richtext' | 'select' | 'multi_select' | 'date' | 'boolean' | 'currency';

export interface FieldConfig {
  options?: string[]; // For select/multi_select
  required?: boolean;
  min_value?: number; // For number
  max_value?: number;
  currency_code?: string; // For currency
  decimal_places?: number;
  allow_custom_values?: boolean; // For select fields
  placeholder?: string;
  help_text?: string;
  thousands_separator?: string;
  prefix?: string;
  suffix?: string;
  max_length?: number; // For text/richtext
}

export interface CustomFieldDefinition {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  field_type: FieldType;
  field_config: FieldConfig;
  applicable_entity_types?: string[]; // null means all types
  display_order: number;
  is_active: boolean;
  created_by: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CustomFieldValue {
  id: string;
  field_definition_id: string;
  field_name: string;
  field_description?: string;
  field_type: FieldType;
  field_config: FieldConfig;
  workspace_id: string;
  workspace_name: string;
  entity_type: string;
  entity_id: string;
  entity_display_name?: string;
  value: any; // Flexible value storage
  created_by: string;
  created_by_name?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
}

export interface CreateCustomFieldDefinitionRequest {
  organization_id: string;
  name: string;
  description?: string;
  field_type: FieldType;
  field_config?: FieldConfig;
  applicable_entity_types?: string[];
  display_order?: number;
}

export interface UpdateCustomFieldDefinitionRequest {
  name?: string;
  description?: string;
  field_config?: FieldConfig;
  applicable_entity_types?: string[];
  display_order?: number;
  is_active?: boolean;
}

export interface CreateCustomFieldValueRequest {
  field_definition_id: string;
  entity_type: string;
  entity_id: string;
  entity_display_name?: string;
  workspace_ids: string[];
  value: any;
}

export interface UpdateCustomFieldValueRequest {
  value: any;
}

export interface Workspace {
  id: string;
  name: string;
  owner_org_id?: string;
}

export interface CustomFieldsResponse {
  success: boolean;
  values: CustomFieldValue[];
  workspaces: Workspace[];
}

export interface CustomFieldDefinitionsResponse {
  success: boolean;
  definitions: CustomFieldDefinition[];
}

export interface CustomFieldValueCountResponse {
  success: boolean;
  count: number;
}

// Field type configuration for UI
export const FIELD_TYPE_CONFIG: Record<FieldType, {
  displayName: string;
  icon: string;
  description: string;
  defaultConfig: FieldConfig;
}> = {
  text: {
    displayName: 'Text',
    icon: 'Type',
    description: 'Single or multi-line text input',
    defaultConfig: { placeholder: 'Enter text...', required: false }
  },
  number: {
    displayName: 'Number',
    icon: 'Hash',
    description: 'Numeric values',
    defaultConfig: { min_value: 0, required: false }
  },
  richtext: {
    displayName: 'Rich Text',
    icon: 'FileText',
    description: 'Formatted text with rich editor',
    defaultConfig: { placeholder: 'Enter formatted text...', max_length: 3000, required: false }
  },
  select: {
    displayName: 'Select',
    icon: 'List',
    description: 'Single selection from predefined options',
    defaultConfig: { options: [], required: false, allow_custom_values: false }
  },
  multi_select: {
    displayName: 'Multi-Select',
    icon: 'ListChecks',
    description: 'Multiple selections from predefined options',
    defaultConfig: { options: [], required: false }
  },
  date: {
    displayName: 'Date',
    icon: 'Calendar',
    description: 'Date picker',
    defaultConfig: { required: false }
  },
  boolean: {
    displayName: 'Yes/No',
    icon: 'ToggleLeft',
    description: 'True/false toggle',
    defaultConfig: { required: false }
  },
  currency: {
    displayName: 'Currency',
    icon: 'DollarSign',
    description: 'Monetary values',
    defaultConfig: { 
      currency_code: 'USD', 
      decimal_places: 2, 
      min_value: 0,
      thousands_separator: ',',
      prefix: '$',
      required: false 
    }
  }
};


